import { useCallback, useEffect, useRef, useState } from 'react'
import JSZip from 'jszip'
import { useAuthenticatedFetch, fileToBase64 } from '../lib/api'

interface Dataset {
  id: string
  user_id: string
  name: string
  description: string | null
  photo_count: number
  lora_status:
    | 'draft'
    | 'photos_uploaded'
    | 'training'
    | 'ready'
    | 'failed'
    | string
  trigger_word: string | null
  lora_destination: string | null
  lora_version: string | null
  training_started_at: string | null
  training_finished_at: string | null
  lora_error_message: string | null
  created_at: string
}

const ACCEPTED = ['image/jpeg', 'image/png', 'image/webp']

export default function Datasets() {
  const authenticatedFetch = useAuthenticatedFetch()
  const [datasets, setDatasets] = useState<Dataset[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const [selected, setSelected] = useState<Dataset | null>(null)

  const refresh = useCallback(async () => {
    setLoading(true)
    try {
      const res = await authenticatedFetch('/api/datasets')
      const { data } = (await res.json()) as { data: Dataset[] }
      setDatasets(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load datasets')
    } finally {
      setLoading(false)
    }
  }, [authenticatedFetch])

  useEffect(() => {
    refresh()
  }, [refresh])

  // Poll any training datasets every 30s
  useEffect(() => {
    const trainingIds = datasets
      .filter((d) => d.lora_status === 'training')
      .map((d) => d.id)
    if (trainingIds.length === 0) return
    const t = setInterval(async () => {
      for (const id of trainingIds) {
        try {
          const res = await authenticatedFetch(`/api/datasets/${id}`)
          const { data } = (await res.json()) as { data: Dataset }
          setDatasets((prev) => prev.map((d) => (d.id === id ? data : d)))
          if (selected?.id === id) setSelected(data)
        } catch {
          // ignore
        }
      }
    }, 30_000)
    return () => clearInterval(t)
  }, [datasets, authenticatedFetch, selected?.id])

  const handleCreate = useCallback(async () => {
    const name = newName.trim()
    if (!name) return
    setCreating(true)
    setError(null)
    try {
      const res = await authenticatedFetch('/api/datasets', {
        method: 'POST',
        body: JSON.stringify({ name }),
      })
      const { data } = (await res.json()) as { data: Dataset }
      setDatasets((prev) => [data, ...prev])
      setSelected(data)
      setNewName('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Create failed')
    } finally {
      setCreating(false)
    }
  }, [authenticatedFetch, newName])

  const handleDelete = useCallback(
    async (id: string) => {
      if (!window.confirm('Delete this dataset? Trained LoRA stays on Replicate.'))
        return
      try {
        await authenticatedFetch(`/api/datasets/${id}`, { method: 'DELETE' })
        setDatasets((prev) => prev.filter((d) => d.id !== id))
        if (selected?.id === id) setSelected(null)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Delete failed')
      }
    },
    [authenticatedFetch, selected?.id],
  )

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-5xl mx-auto p-6 space-y-6">
        <div className="space-y-1">
          <h1 className="text-2xl font-bold text-text-primary">Datasets</h1>
          <p className="text-sm text-text-secondary">
            Train custom FLUX LoRAs from your photos. 10–25 images works best.
            You'll need a Replicate API key and a few dollars of credit.
          </p>
        </div>

        {/* Create form */}
        <div className="bg-bg-card border border-border rounded-xl p-4 flex items-center gap-3">
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Dataset name (e.g. Frank's Face v1)"
            className="input-glow flex-1 bg-bg-secondary border border-border rounded-lg px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-neon transition-all"
            disabled={creating}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCreate()
            }}
          />
          <button
            onClick={handleCreate}
            disabled={creating || !newName.trim()}
            className="px-4 py-2 text-sm font-semibold rounded-lg bg-neon text-bg-primary hover:bg-neon-dim transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {creating ? 'Creating…' : 'New dataset'}
          </button>
        </div>

        {error && (
          <div className="px-4 py-3 rounded-lg bg-error/10 border border-error/30 text-error text-sm">
            {error}
          </div>
        )}

        {/* List */}
        {loading ? (
          <p className="text-text-muted text-sm">Loading…</p>
        ) : datasets.length === 0 ? (
          <div className="text-center py-12 space-y-3">
            <p className="text-text-secondary">
              No datasets yet. Create one to start training a custom LoRA.
            </p>
          </div>
        ) : (
          <div className="grid gap-3">
            {datasets.map((d) => (
              <DatasetRow
                key={d.id}
                dataset={d}
                onOpen={() => setSelected(d)}
                onDelete={() => handleDelete(d.id)}
              />
            ))}
          </div>
        )}
      </div>

      {selected && (
        <DatasetDetailModal
          dataset={selected}
          onClose={() => setSelected(null)}
          onUpdate={(updated) => {
            setSelected(updated)
            setDatasets((prev) =>
              prev.map((d) => (d.id === updated.id ? updated : d)),
            )
          }}
        />
      )}
    </div>
  )
}

function statusLabel(s: string): { label: string; color: string } {
  switch (s) {
    case 'draft':
      return { label: 'Draft — needs photos', color: 'text-text-muted' }
    case 'photos_uploaded':
      return { label: 'Ready to train', color: 'text-neon' }
    case 'training':
      return { label: 'Training…', color: 'text-warning' }
    case 'ready':
      return { label: 'Ready', color: 'text-neon' }
    case 'failed':
      return { label: 'Failed', color: 'text-error' }
    default:
      return { label: s, color: 'text-text-muted' }
  }
}

function DatasetRow({
  dataset,
  onOpen,
  onDelete,
}: {
  dataset: Dataset
  onOpen: () => void
  onDelete: () => void
}) {
  const { label, color } = statusLabel(dataset.lora_status)
  return (
    <div
      onClick={onOpen}
      className="bg-bg-card border border-border hover:border-border-hover rounded-xl p-4 cursor-pointer transition-colors flex items-center gap-4"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-semibold text-text-primary truncate">
            {dataset.name}
          </h3>
          {dataset.trigger_word && (
            <code className="text-[10px] font-mono text-text-muted bg-bg-secondary px-1.5 py-0.5 rounded">
              {dataset.trigger_word}
            </code>
          )}
        </div>
        <p className={`text-xs ${color}`}>
          {label} · {dataset.photo_count} photos
        </p>
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation()
          onDelete()
        }}
        className="text-[11px] text-text-muted hover:text-error px-2 py-1 cursor-pointer"
      >
        Delete
      </button>
    </div>
  )
}

function DatasetDetailModal({
  dataset,
  onClose,
  onUpdate,
}: {
  dataset: Dataset
  onClose: () => void
  onUpdate: (d: Dataset) => void
}) {
  const authenticatedFetch = useAuthenticatedFetch()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [files, setFiles] = useState<File[]>([])
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null)

  const onPickFiles = useCallback((picked: FileList | null) => {
    if (!picked) return
    const list = Array.from(picked).filter((f) => ACCEPTED.includes(f.type))
    setFiles(list.slice(0, 25))
  }, [])

  const handleZipUpload = useCallback(async () => {
    if (files.length < 5) {
      setMsg({ kind: 'err', text: 'Add at least 5 photos for a usable LoRA.' })
      return
    }
    setBusy(true)
    setMsg(null)
    try {
      const zip = new JSZip()
      files.forEach((f, i) => {
        const ext = f.name.split('.').pop()?.toLowerCase() ?? 'jpg'
        zip.file(`photo_${String(i + 1).padStart(3, '0')}.${ext}`, f)
      })
      const blob = await zip.generateAsync({ type: 'blob' })
      const data_base64 = await fileToBase64(new File([blob], 'training.zip'))
      const res = await authenticatedFetch(`/api/datasets/${dataset.id}/upload-zip`, {
        method: 'POST',
        body: JSON.stringify({ data_base64, photo_count: files.length }),
      })
      const { data } = (await res.json()) as { data: Dataset }
      onUpdate(data)
      setFiles([])
      setMsg({ kind: 'ok', text: 'Photos uploaded. Ready to train.' })
    } catch (err) {
      setMsg({
        kind: 'err',
        text: err instanceof Error ? err.message : 'Upload failed',
      })
    } finally {
      setBusy(false)
    }
  }, [authenticatedFetch, dataset.id, files, onUpdate])

  const handleStartTraining = useCallback(async () => {
    if (
      !window.confirm(
        'Start training? Costs 200 studio credits (or free if you have a personal Replicate key). Takes 15-30 min.',
      )
    )
      return
    setBusy(true)
    setMsg(null)
    try {
      const res = await authenticatedFetch(`/api/datasets/${dataset.id}/train`, {
        method: 'POST',
      })
      const { data } = (await res.json()) as { data: Dataset }
      onUpdate(data)
      setMsg({ kind: 'ok', text: 'Training started. Check back in ~20 min.' })
    } catch (err) {
      setMsg({
        kind: 'err',
        text: err instanceof Error ? err.message : 'Training start failed',
      })
    } finally {
      setBusy(false)
    }
  }, [authenticatedFetch, dataset.id, onUpdate])

  const { label, color } = statusLabel(dataset.lora_status)
  const canUpload = dataset.lora_status === 'draft'
  const canTrain = dataset.lora_status === 'photos_uploaded'

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl bg-bg-card border border-border rounded-2xl overflow-hidden flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-border flex items-start justify-between">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-text-primary truncate">
              {dataset.name}
            </h2>
            <p className={`text-xs ${color} mt-0.5`}>{label}</p>
            {dataset.trigger_word && (
              <p className="text-[11px] text-text-muted mt-2">
                Trigger word:{' '}
                <code className="font-mono text-text-secondary">
                  {dataset.trigger_word}
                </code>{' '}
                · include this in prompts to activate the LoRA
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-text-muted hover:text-text-primary p-2 cursor-pointer"
            aria-label="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Photo upload step */}
          {canUpload && (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-text-primary">
                Step 1 — Upload training photos
              </h3>
              <p className="text-xs text-text-secondary">
                Pick 10–25 photos (jpg/png/webp). Variety helps: different
                angles, lighting, and expressions.
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED.join(',')}
                multiple
                onChange={(e) => onPickFiles(e.target.files)}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={busy}
                className="w-full px-4 py-3 rounded-lg border-2 border-dashed border-border hover:border-neon hover:bg-neon/5 text-text-secondary hover:text-neon transition-colors cursor-pointer"
              >
                {files.length > 0
                  ? `${files.length} photos selected — click to change`
                  : 'Click to pick photos'}
              </button>
              {files.length > 0 && (
                <div className="grid grid-cols-5 gap-2">
                  {files.slice(0, 10).map((f, i) => (
                    <img
                      key={i}
                      src={URL.createObjectURL(f)}
                      alt=""
                      className="w-full aspect-square object-cover rounded border border-border"
                    />
                  ))}
                  {files.length > 10 && (
                    <div className="w-full aspect-square rounded border border-border bg-bg-secondary flex items-center justify-center text-text-muted text-xs">
                      +{files.length - 10}
                    </div>
                  )}
                </div>
              )}
              <button
                onClick={handleZipUpload}
                disabled={busy || files.length < 5}
                className="w-full px-4 py-2 text-sm font-semibold rounded-lg bg-neon text-bg-primary hover:bg-neon-dim transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {busy ? 'Uploading…' : 'Upload photos'}
              </button>
            </div>
          )}

          {/* Train step */}
          {canTrain && (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-text-primary">
                Step 2 — Train the LoRA
              </h3>
              <p className="text-xs text-text-secondary">
                Training runs on Replicate using ostris/flux-dev-lora-trainer
                (1000 steps, rank 16). Takes 15–30 minutes. You can close this
                page; we'll keep checking.
              </p>
              <div className="flex items-center gap-2 text-[11px] text-text-muted">
                <span className="inline-flex items-center gap-1.5 font-mono uppercase tracking-wider px-2 py-1 rounded border border-neon/30 bg-neon/5 text-neon">
                  200 credits
                </span>
                <span>· or free with your own Replicate key (BYOK)</span>
              </div>
              <button
                onClick={handleStartTraining}
                disabled={busy}
                className="w-full px-4 py-2 text-sm font-semibold rounded-lg bg-neon text-bg-primary hover:bg-neon-dim transition-colors cursor-pointer disabled:opacity-50"
              >
                {busy ? 'Starting…' : 'Start training'}
              </button>
            </div>
          )}

          {/* Training in progress */}
          {dataset.lora_status === 'training' && (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-text-primary">
                Training in progress
              </h3>
              <div className="rounded-lg border border-border bg-bg-secondary p-4 text-center">
                <div className="w-6 h-6 mx-auto rounded-full border-2 border-neon border-t-transparent animate-spin mb-2" />
                <p className="text-xs text-text-secondary">
                  Training started{' '}
                  {dataset.training_started_at
                    ? new Date(dataset.training_started_at).toLocaleTimeString()
                    : ''}
                  . This typically takes 15–30 minutes. The page polls every
                  30s.
                </p>
              </div>
            </div>
          )}

          {/* Ready */}
          {dataset.lora_status === 'ready' && (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-neon">
                LoRA ready to use
              </h3>
              <p className="text-xs text-text-secondary">
                Generate from this LoRA in the Studio. Your prompts will be
                automatically prefixed with the trigger word{' '}
                <code className="font-mono text-text-secondary">
                  {dataset.trigger_word}
                </code>
                .
              </p>
              <p className="text-[11px] text-text-muted">
                Destination:{' '}
                <code className="font-mono">{dataset.lora_destination}</code>
              </p>
            </div>
          )}

          {/* Failed */}
          {dataset.lora_status === 'failed' && (
            <div className="rounded-lg border border-error/40 bg-error/5 p-4 space-y-1">
              <h3 className="text-sm font-semibold text-error">
                Training failed
              </h3>
              <p className="text-xs text-text-secondary">
                {dataset.lora_error_message ?? 'Unknown error'}
              </p>
            </div>
          )}

          {msg && (
            <p
              className={`text-[11px] ${
                msg.kind === 'ok' ? 'text-neon' : 'text-error'
              }`}
            >
              {msg.text}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
